import { TFunction } from 'react-i18next';
import List from '@mui/material/List';
import Box from '@mui/material/Box';
import {
  DocumentTextIcon,
  PasswordCheckIcon,
  BuildingsIcon,
  MessageTextIcon,
  HomeIcon,
  VerticalSlashIcon,
  ShipIcon,
  DiscountCircle,
  ClipboardTickIcon,
  LogsIcon,
  FeedbackIcon,
  BankingIcon,
  CashControl,
  RevolvingCreditLineIcon,
  RevolvingCreditLineClientIcon,
  ArrangeHorizontalSquareIcon,
} from '../../components/Icons';
import { useAppSelector } from '../../store';
import { useFactoringCreditLineList } from '../../hooks/useFactoringCreditLine';
import { CreditLineProductType } from '../../types/factoringCreditLine';
import {
  BANKING,
  BANKING_CONNECTED_ACCOUNTS,
  BANKING_RECENT_TRANSACTIONS,
  BANKING_REPORTS,
  BANKING_TRANSACTIONS,
  BUYER_SUMMARY,
  PAYMENTS,
  CONTACT,
  DASHBOARD,
  TRANSFERS,
  INVOICES,
  INVOICE_FINANCING,
  LEGAL_ASSISTANT,
  LLC,
  POST_CC,
  REPORTING,
  TRACKING,
  LOGS,
  BANKING_RECIPIENTS,
  BANKING_TRANSFERS,
  FINANCE_REQUEST,
  FEEDBACK,
  COMPLIANCE,
  ADMIN_COMPLIANCE,
  ADMIN_REGISTERED_AGENT,
  BANKING_CONVERSIONS,
  BANKING_CARDS,
  BANKING_GLOBAL_ACCOUNTS,
  BANKING_ADMIN_CARDS,
  BANKING_ADMIN_RFIS,
  BANKING_LOW_ACTIVITY_ACCOUNTS,
  BANKING_REPORTING,
  BANKING_RECURRING_PAYMENTS,
  BANKING_RFIS,
  REVOLVING_CREDIT_LINE,
  INVOICING,
  INVOICING_CLIENTS,
  INVOICING_PRODUCTS,
  INVOICING_LIST_INVOICES,
  FACTORING_CREDIT_LINE,
  FACTORING_CREDIT_LINE_INVOICES,
  ADMIN_DASHBOARD,
  BANKING_CASH_CONTROL_CONNECTED_ACCOUNTS,
  CASH_CONTROL_LENDERS,
  LENDER_DASHBOARD,
  LENDER_CLIENTS,
  LENDER_TRANSACTIONS,
  REVOLVING_CREDIT_LINE_ADMIN,
  ADMIN_PARTNERS,
} from '../../constants/auth';
import { US_COUNTRY_CODE } from '../../constants/data';
import AppMenuItem, { AppMenuItemType } from './AppMenuItem';
import usePermissions from '../../hooks/usePermissions';
import { useTranslation } from '../../hooks/i18n';
import { useInvoicingConnectionStatus, INVOICING_STATUS_CONNECTED } from '../../hooks/useInvoicing';
import { canAccessInvoicing } from '../../utils/invoicing';
import { ADMIN_FEEDBACK_INFORMATION } from '../../constants/permissions';
import { Company } from '../../types/companyUser';
import MultiCompanySelector from '../../containers/MultiCompanySelector';

export type SidebarElement = AppMenuItemType & {
  items?: AppMenuItemType[];
};

export const defaultLinks = (
  t: TFunction,
  company?: Company,
  isInvoicingConnected?: boolean,
  hasFactoringLine = false
): SidebarElement[] => {
  // US companies get the Revolving Credit Line (RCL); everyone else gets the
  // Factoring Credit Line (Factoring Lite). The two are mutually exclusive.
  const isUsCompany = company?.country === US_COUNTRY_CODE;
  return [
    { name: 'dashboard', route: DASHBOARD, Icon: HomeIcon, trackingName: 'Home' },
    {
      name: 'banking',
      route: BANKING,
      Icon: BankingIcon,
      trackingName: 'Banking',
      items: [
        ...(company?.productStatus.readyForBanking && company?.productStatus.hasStartedBanking
          ? [
              {
                name: 'globalAccounts',
                route: BANKING_GLOBAL_ACCOUNTS,
                Icon: VerticalSlashIcon,
                trackingName: 'Banking Global Accounts',
              },
              {
                name: 'transactions',
                route: BANKING_TRANSACTIONS,
                Icon: VerticalSlashIcon,
                trackingName: 'Banking Transactions',
              },
              {
                name: 'transfers',
                route: BANKING_TRANSFERS,
                Icon: VerticalSlashIcon,
                trackingName: 'Banking Transfers',
              },
              {
                name: 'recipients',
                route: BANKING_RECIPIENTS,
                Icon: VerticalSlashIcon,
                trackingName: 'Banking Recipients',
              },
              {
                name: 'conversions',
                route: BANKING_CONVERSIONS,
                Icon: VerticalSlashIcon,
                trackingName: 'Banking Conversions',
              },
              {
                name: 'cards',
                route: BANKING_CARDS,
                Icon: VerticalSlashIcon,
                trackingName: 'Banking Cards',
              },
              {
                name: 'recurringPayments',
                route: BANKING_RECURRING_PAYMENTS,
                Icon: VerticalSlashIcon,
                trackingName: 'Banking Recurring Payments',
              },
              {
                name: 'reporting',
                route: BANKING_REPORTING,
                Icon: VerticalSlashIcon,
                trackingName: 'Banking Reporting',
              },
              {
                name: 'rfis',
                route: BANKING_RFIS,
                Icon: VerticalSlashIcon,
                trackingName: 'Banking RFIs',
              },
            ]
          : []),
      ],
    },
    ...(isUsCompany
      ? [
          {
            name: 'revolvingCredit',
            route: REVOLVING_CREDIT_LINE,
            Icon: RevolvingCreditLineClientIcon,
            trackingName: 'Revolving Credit',
          },
        ]
      : [
          // Non-US companies use the Factoring Credit Line instead of the RCL. The section always
          // shows so a client can reach the request page; the Invoices sub-page only appears once
          // the company has an active factoring credit line.
          {
            name: 'factoringCreditLine',
            route: FACTORING_CREDIT_LINE,
            Icon: RevolvingCreditLineClientIcon,
            trackingName: 'Factoring Credit Line',
            items: hasFactoringLine
              ? [
                  {
                    name: 'invoices',
                    route: FACTORING_CREDIT_LINE_INVOICES,
                    Icon: VerticalSlashIcon,
                    trackingName: 'Factoring Invoices',
                  },
                ]
              : [],
          },
        ]),
    ...(canAccessInvoicing(company)
      ? [
          {
            name: 'invoicing',
            route: INVOICING,
            Icon: DocumentTextIcon,
            trackingName: 'Invoicing',
            items:
              company?.productStatus.readyForInvoicing && isInvoicingConnected
                ? [
                    {
                      name: 'invoices',
                      route: INVOICING_LIST_INVOICES,
                      Icon: VerticalSlashIcon,
                      trackingName: 'Invoices List',
                    },
                    {
                      name: 'clients',
                      route: INVOICING_CLIENTS,
                      Icon: VerticalSlashIcon,
                      trackingName: 'Invoicing Clients',
                    },
                    {
                      name: 'products',
                      route: INVOICING_PRODUCTS,
                      Icon: VerticalSlashIcon,
                      trackingName: 'Invoicing Products',
                    },
                  ]
                : [],
          },
        ]
      : []),
    {
      name: 'usLLC',
      route: LLC,
      Icon: BuildingsIcon,
      trackingName: 'LLC',
    },
    {
      name: 'compliance',
      route: COMPLIANCE,
      trackingName: 'Compliance',
      Icon: DiscountCircle,
    },
    {
      name: 'application',
      route: INVOICE_FINANCING,
      Icon: DocumentTextIcon,
      trackingName: 'Invoice Financing',
      items: company?.productStatus.readyForFactoring
        ? [
            {
              name: 'financeRequest',
              route: FINANCE_REQUEST,
              Icon: VerticalSlashIcon,
              trackingName: 'Finance Request',
            },
            {
              name: 'invoices',
              route: INVOICES,
              Icon: VerticalSlashIcon,
              trackingName: 'Invoices',
            },
            {
              name: 'transfers',
              route: TRANSFERS,
              Icon: VerticalSlashIcon,
              trackingName: 'Transfers',
            },
            {
              name: 'payments',
              route: PAYMENTS,
              Icon: VerticalSlashIcon,
              trackingName: 'Payments',
            },
            {
              name: 'buyerSummary',
              route: BUYER_SUMMARY,
              Icon: VerticalSlashIcon,
              trackingName: 'Buyer Summary',
            },
            {
              name: 'reporting',
              route: REPORTING,
              Icon: VerticalSlashIcon,
              trackingName: 'Reporting',
            },
          ]
        : [],
    },
    {
      name: 'getHelp',
      route: CONTACT,
      Icon: MessageTextIcon,
      trackingName: 'Contact',
    },
  ];
};

export const lenderLinks = (t: TFunction): SidebarElement[] => [
  { name: 'dashboard', route: LENDER_DASHBOARD, Icon: HomeIcon, trackingName: 'Lender - Dashboard' },
  { name: 'clients', route: LENDER_CLIENTS, Icon: BuildingsIcon, trackingName: 'Lender - Clients' },
  {
    name: 'transactions',
    route: LENDER_TRANSACTIONS,
    Icon: BankingIcon,
    trackingName: 'Lender - Transactions',
  },
];

export const adminLinks = (t: TFunction): SidebarElement[] => [
  { name: 'dashboard', route: ADMIN_DASHBOARD, Icon: HomeIcon, trackingName: 'Home' },
  {
    name: 'marta',
    route: LEGAL_ASSISTANT,
    isAbsoluteRoute: true,
    trackingName: 'Legal assistant',
    Icon: PasswordCheckIcon,
  },
  {
    name: 'creditCommittee',
    route: POST_CC,
    trackingName: 'Post Credit Committee',
    Icon: DocumentTextIcon,
  },
  {
    name: 'bankingAdmin',
    route: BANKING_REPORTS,
    trackingName: 'Banking Connected Accounts',
    Icon: BankingIcon,
    items: [
      {
        name: 'accounts',
        route: BANKING_CONNECTED_ACCOUNTS,
        trackingName: 'Connected Accounts',
        Icon: VerticalSlashIcon,
      },
      {
        name: 'transactions',
        route: BANKING_RECENT_TRANSACTIONS,
        trackingName: 'Recent Transactions',
        Icon: VerticalSlashIcon,
      },
      {
        name: 'cards',
        route: BANKING_ADMIN_CARDS,
        trackingName: 'Cards',
        Icon: VerticalSlashIcon,
      },
      {
        name: 'rfis',
        route: BANKING_ADMIN_RFIS,
        trackingName: 'RFIs',
        Icon: VerticalSlashIcon,
      },
      {
        name: 'lowActivityAccounts',
        route: BANKING_LOW_ACTIVITY_ACCOUNTS,
        trackingName: 'Low Activity Accounts',
        Icon: VerticalSlashIcon,
      },
    ],
  },
  {
    name: 'cashControl',
    route: BANKING_CASH_CONTROL_CONNECTED_ACCOUNTS,
    trackingName: 'Cash Control',
    Icon: CashControl,
    items: [
      {
        name: 'cashControlAccounts',
        route: BANKING_CASH_CONTROL_CONNECTED_ACCOUNTS,
        trackingName: 'Cash Control Accounts',
        Icon: VerticalSlashIcon,
      },
      {
        name: 'cashControlLenders',
        route: CASH_CONTROL_LENDERS,
        trackingName: 'Cash Control Lenders',
        Icon: VerticalSlashIcon,
      },
    ],
  },
  {
    name: 'compliance',
    route: ADMIN_COMPLIANCE,
    trackingName: 'Compliance',
    Icon: DiscountCircle,
  },
  {
    name: 'registeredAgent',
    route: ADMIN_REGISTERED_AGENT,
    trackingName: 'Registered Agent',
    Icon: (props: any) => <ClipboardTickIcon {...props} width={20} height={20} />,
  },
  {
    name: 'partners',
    route: ADMIN_PARTNERS,
    trackingName: 'Partners',
    Icon: ArrangeHorizontalSquareIcon,
  },
  {
    name: 'revolvingCreditLineAdmin',
    route: REVOLVING_CREDIT_LINE_ADMIN,
    trackingName: 'Revolving Credit Line Admin',
    Icon: RevolvingCreditLineIcon,
  },
  {
    name: 'tracking',
    route: TRACKING,
    trackingName: 'Tracking',
    Icon: ShipIcon,
  },
  {
    name: 'logs',
    route: LOGS,
    trackingName: 'Logs',
    Icon: LogsIcon,
  },
  {
    name: 'feedback',
    route: FEEDBACK,
    trackingName: 'Feedback',
    Icon: FeedbackIcon,
    permissions: [ADMIN_FEEDBACK_INFORMATION],
  },
];

type AppMenuProps = {
  isAdmin: boolean;
  isLender?: boolean;
  handleOnClickItemCallback: () => void;
  isDrawerOpened?: boolean;
  openDrawer: () => void;
};

const AppMenu = ({
  isAdmin,
  isLender = false,
  handleOnClickItemCallback,
  isDrawerOpened,
  openDrawer,
}: AppMenuProps) => {
  const selectedCompany = useAppSelector((state) => state.companyUser.companyUser?.selectedCompany);
  const { t } = useTranslation('compliance');
  const { HasPermissions } = usePermissions();

  const { data: invoicingConnection } = useInvoicingConnectionStatus(
    selectedCompany?.productStatus.readyForInvoicing ? selectedCompany.id : undefined
  );
  const isInvoicingConnected = invoicingConnection?.status === INVOICING_STATUS_CONNECTED;
  // The Factoring "Invoices" sub-item only appears once the company has an active factoring
  // credit line (mirrors the page gating: no line -> request page, with line -> dashboard).
  const { data: factoringCreditLineList } = useFactoringCreditLineList(
    !isAdmin && !isLender && selectedCompany?.country !== US_COUNTRY_CODE ? selectedCompany?.id : undefined
  );
  const hasFactoringLine = !!factoringCreditLineList?.results?.find(
    (creditLine) => creditLine.productType === CreditLineProductType.FACTORING
  );

  const excludeLinksByPermissions = (linksList: SidebarElement[]) =>
    linksList.filter((link) => !link.permissions || HasPermissions(link.permissions));

  const getLinksForUserType = () => {
    if (isAdmin) return excludeLinksByPermissions(adminLinks(t));
    if (isLender) return excludeLinksByPermissions(lenderLinks(t));

    return excludeLinksByPermissions(
      defaultLinks(t, selectedCompany, isInvoicingConnected, hasFactoringLine)
    );
  };

  return (
    <>
      {!isAdmin && !isLender && (
        <Box>
          <MultiCompanySelector isCollapsed={!isDrawerOpened} openDrawer={openDrawer} />
        </Box>
      )}
      <List>
        {getLinksForUserType().map((item) => {
          return (
            <AppMenuItem
              {...item}
              key={item.name}
              onClickItemCallback={handleOnClickItemCallback}
              isDrawerOpened={isDrawerOpened}
            />
          );
        })}
      </List>
    </>
  );
};

export default AppMenu;
